import React, { useEffect, useState } from 'react';
import { useAlert } from '../ui/alertProvider';
import { ClinicProfileAPI, ClinicProfile } from '../../Services/clinicProfileApi';
import './clinicSettings.css';

interface ClinicSettingsFormProps {
  siteId: number;
  onSave?: () => void;
}

const ClinicSettingsForm: React.FC<ClinicSettingsFormProps> = ({ siteId, onSave }) => {
  const { showAlert } = useAlert();
  const [clinic, setClinic] = useState<ClinicProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'basic' | 'hours' | 'availability'>('basic');

  // Form state
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [workingHours, setWorkingHours] = useState('');
  const [shortDescription, setShortDescription] = useState('');
  const [operatingStatus, setOperatingStatus] = useState('');
  const [breakTimeStart, setBreakTimeStart] = useState('');
  const [breakTimeEnd, setBreakTimeEnd] = useState('');
  const [closedDates, setClosedDates] = useState('');
  const [appointmentSlots, setAppointmentSlots] = useState('');

  // Load clinic data
  useEffect(() => {
    loadClinic();
  }, [siteId]);

  const loadClinic = async () => {
    try {
      setLoading(true);
      const data = await ClinicProfileAPI.getClinicBySiteId(siteId);
      setClinic(data);
      populateFormFields(data);
      showAlert('Clinic loaded', 'success');
    } catch (error: any) {
      if (error?.status === 404) {
        // Clinic doesn't exist yet
        setClinic(null);
        showAlert('No clinic profile found. Create a new one.', 'info');
      } else {
        console.error('Error loading clinic:', error);
        showAlert(`Error loading clinic: ${error?.data || error.message}`, 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const populateFormFields = (data: ClinicProfile) => {
    setAddress(data.address || '');
    setPhone(data.phone || '');
    setWorkingHours(data.workingHours || '');
    setShortDescription(data.shortDescription || '');
    setOperatingStatus(data.operatingStatus || '');
    setBreakTimeStart(data.breakTimeStart || '');
    setBreakTimeEnd(data.breakTimeEnd || '');
    setClosedDates(data.closedDates || '');
    setAppointmentSlots(data.appointmentSlots || '');
  };

  const handleSaveBasic = async () => {
    try {
      setSaving(true);

      if (!clinic) {
        // Create new clinic
        const newClinic: ClinicProfile = {
          id: 0,
          siteId,
          address,
          phone,
          workingHours,
          shortDescription,
          operatingStatus,
          breakTimeStart,
          breakTimeEnd,
          closedDates,
          appointmentSlots,
          createdAt: new Date().toISOString(),
        };

        const created = await ClinicProfileAPI.createClinic(newClinic);
        setClinic(created);
        showAlert('Clinic created successfully', 'success');
      } else {
        // Update existing clinic
        const updated: ClinicProfile = {
          ...clinic,
          address,
          phone,
          workingHours,
          shortDescription,
        };

        const result = await ClinicProfileAPI.updateClinic(clinic.id, updated);
        setClinic(result);
        showAlert('Clinic updated successfully', 'success');
      }

      if (onSave) onSave();
    } catch (error: any) {
      console.error('Error saving clinic:', error);
      showAlert(`Error saving clinic: ${error?.data || error.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveHours = async () => {
    if (!clinic) {
      showAlert('Clinic must be created first', 'error');
      return;
    }

    try {
      setSaving(true);
      const result = await ClinicProfileAPI.updateClinicHours(clinic.id, {
        workingHours,
        operatingStatus,
        breakTimeStart,
        breakTimeEnd,
      });
      setClinic(result);
      showAlert('Clinic hours updated successfully', 'success');
      if (onSave) onSave();
    } catch (error: any) {
      console.error('Error updating clinic hours:', error);
      showAlert(`Error updating hours: ${error?.data || error.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAvailability = async () => {
    if (!clinic) {
      showAlert('Clinic must be created first', 'error');
      return;
    }

    try {
      setSaving(true);
      const result = await ClinicProfileAPI.updateClinicAvailability(clinic.id, {
        closedDates,
        appointmentSlots,
      });
      setClinic(result);
      showAlert('Clinic availability updated successfully', 'success');
      if (onSave) onSave();
    } catch (error: any) {
      console.error('Error updating clinic availability:', error);
      showAlert(`Error updating availability: ${error?.data || error.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="clinic-settings loading">Loading clinic settings...</div>;
  }

  return (
    <div className="clinic-settings">
      <h2>Clinic Settings</h2>
      <p className="site-id-info">Site ID: {siteId}</p>

      <div className="tabs">
        <button
          className={`tab-button ${activeTab === 'basic' ? 'active' : ''}`}
          onClick={() => setActiveTab('basic')}
        >
          Basic Info
        </button>
        <button
          className={`tab-button ${activeTab === 'hours' ? 'active' : ''}`}
          onClick={() => setActiveTab('hours')}
        >
          Hours & Operating
        </button>
        <button
          className={`tab-button ${activeTab === 'availability' ? 'active' : ''}`}
          onClick={() => setActiveTab('availability')}
        >
          Availability
        </button>
      </div>

      {/* BASIC INFO TAB */}
      {activeTab === 'basic' && (
        <div className="tab-content">
          <h3>Basic Information</h3>

          <div className="form-group">
            <label htmlFor="address">Address</label>
            <input
              id="address"
              type="text"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Clinic address"
            />
          </div>

          <div className="form-group">
            <label htmlFor="phone">Phone</label>
            <input
              id="phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Clinic phone number"
            />
          </div>

          <div className="form-group">
            <label htmlFor="shortDescription">Short Description</label>
            <textarea
              id="shortDescription"
              value={shortDescription}
              onChange={(e) => setShortDescription(e.target.value)}
              placeholder="Brief clinic description"
              rows={4}
            />
          </div>

          <div className="form-group">
            <label htmlFor="workingHours">Working Hours</label>
            <textarea
              id="workingHours"
              value={workingHours}
              onChange={(e) => setWorkingHours(e.target.value)}
              placeholder="E.g., Monday-Friday: 09:00-17:00, Saturday: 10:00-14:00"
              rows={4}
            />
          </div>

          <button className="save-button" onClick={handleSaveBasic} disabled={saving}>
            {saving ? 'Saving...' : 'Save Basic Info'}
          </button>
        </div>
      )}

      {/* HOURS & OPERATING TAB */}
      {activeTab === 'hours' && (
        <div className="tab-content">
          <h3>Hours & Operating Status</h3>

          <div className="form-group">
            <label htmlFor="operatingStatus">Operating Status</label>
            <select
              id="operatingStatus"
              value={operatingStatus}
              onChange={(e) => setOperatingStatus(e.target.value)}
            >
              <option value="">Select status...</option>
              <option value="Open">Open</option>
              <option value="Closed">Closed</option>
              <option value="By Appointment">By Appointment</option>
              <option value="Emergency Only">Emergency Only</option>
            </select>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="breakTimeStart">Break Time Start (HH:mm)</label>
              <input
                id="breakTimeStart"
                type="time"
                value={breakTimeStart}
                onChange={(e) => setBreakTimeStart(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label htmlFor="breakTimeEnd">Break Time End (HH:mm)</label>
              <input
                id="breakTimeEnd"
                type="time"
                value={breakTimeEnd}
                onChange={(e) => setBreakTimeEnd(e.target.value)}
              />
            </div>
          </div>

          <p className="help-text">Set break times to indicate when the clinic is closed for lunch/break.</p>

          <button className="save-button" onClick={handleSaveHours} disabled={saving}>
            {saving ? 'Saving...' : 'Save Hours & Status'}
          </button>
        </div>
      )}

      {/* AVAILABILITY TAB */}
      {activeTab === 'availability' && (
        <div className="tab-content">
          <h3>Clinic Availability</h3>

          <div className="form-group">
            <label htmlFor="closedDates">Closed Dates (JSON array)</label>
            <textarea
              id="closedDates"
              value={closedDates}
              onChange={(e) => setClosedDates(e.target.value)}
              placeholder='E.g., ["2024-12-25", "2024-12-26"]'
              rows={4}
            />
            <p className="help-text">Enter dates when clinic is closed (holidays, special closures). Use JSON array format.</p>
          </div>

          <div className="form-group">
            <label htmlFor="appointmentSlots">Appointment Slots (JSON)</label>
            <textarea
              id="appointmentSlots"
              value={appointmentSlots}
              onChange={(e) => setAppointmentSlots(e.target.value)}
              placeholder='E.g., {"monday": ["09:00", "10:00"], "tuesday": ["14:00", "15:00"]}'
              rows={4}
            />
            <p className="help-text">Define available appointment slots per day. Use JSON format.</p>
          </div>

          <button className="save-button" onClick={handleSaveAvailability} disabled={saving}>
            {saving ? 'Saving...' : 'Save Availability'}
          </button>
        </div>
      )}

      {clinic && (
        <div className="clinic-info">
          <p>
            <strong>Clinic ID:</strong> {clinic.id}
          </p>
          <p>
            <strong>Created:</strong> {new Date(clinic.createdAt).toLocaleString()}
          </p>
        </div>
      )}
    </div>
  );
};

export default ClinicSettingsForm;
